// common-codes/services/navigation.service.ts
import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { filter } from 'rxjs';

export interface HistoryEntry {
    route: string;
    timestamp: Date;
    data?: any;
}

// Carte de navigation logique - définit où aller depuis chaque page
export interface NavigationMap {
    [currentPage: string]: string; // currentPage -> previousPage
}

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private historyStack: HistoryEntry[] = [];
    private isNavigatingProgrammatically = false;
    private isRouteLoaded = false;


    // Arborescence logique de navigation QR Code
    private qrCodeNavigationMap: NavigationMap = {
        'trucker-checkin': '/home',
        'trucker-checkout': '/home',
        'visitor-type': '/home',
        'checkout-type': '/home',
        'checkout': '/checkout-type',
        'visitor': '/visitor-type',
        'contractor': '/visitor-type',
        'employee': '/visitor-type',
    };

    constructor(
        private router: Router,
        private location: Location
    ) {
        this.setupBrowserHistoryInterception();
    }

    private setupBrowserHistoryInterception() {
        window.addEventListener('popstate', (event) => {
            if (!this.isNavigatingProgrammatically) {
                event.preventDefault();
                event.stopPropagation();

                if (this.canGoBack()) {
                    this.goBack();
                } else {
                    this.goBackLogical();
                }
            }
        }, true);

        window.addEventListener('keydown', (event) => {
            if ((event.altKey && event.key === 'ArrowLeft') ||
                (event.altKey && event.key === 'ArrowRight') ||
                (event.ctrlKey && event.key === '[') ||
                (event.ctrlKey && event.key === ']')) {
                event.preventDefault();
                if (event.key === 'ArrowLeft' || event.key === '[') {
                    this.goBack();
                }
            }
        }, true);
    }

    // Naviguer vers une nouvelle page
    navigateTo(route: string, data?: any) {
        this.isNavigatingProgrammatically = true;

        this.historyStack.push({
            route,
            timestamp: new Date(),
            data
        });

        this.router.navigate([route]).then(() => {
            history.replaceState(
                { navigationServiceManaged: true },
                '',
                route
            );
            this.isNavigatingProgrammatically = false;
        });
    }

    // Navigation précédent avec fallback logique
    goBack(): boolean {
        // Essayer d'abord avec la stack d'historique
        if (this.canGoBack()) {
            return this.goBackFromStack();
        }

        // Fallback : utiliser l'arborescence logique
        return this.goBackLogical();
    }

    // Navigation depuis la stack d'historique
    private goBackFromStack(): boolean {
        if (this.canGoBack()) {
            this.isNavigatingProgrammatically = true;

            this.historyStack.pop();
            const previousEntry = this.historyStack[this.historyStack.length - 1];

            if (previousEntry) {
                this.router.navigate([previousEntry.route]).then(() => {
                    history.replaceState(
                        { navigationServiceManaged: true },
                        '',
                        previousEntry.route
                    );
                    this.isNavigatingProgrammatically = false;
                });
                return true;
            }
        }
        return false;
    }

    // Navigation logique basée sur l'arborescence
    private goBackLogical(): boolean {
        const currentUrl = this.router.url;
        const currentPageName = this.getCurrentPageName();

        if (currentPageName && this.qrCodeNavigationMap[currentPageName]) {
            const targetPage = this.qrCodeNavigationMap[currentPageName];
            const targetRoute = this.buildTargetRoute(currentUrl, targetPage);

            if (targetRoute) {
                this.navigateTo(targetRoute);
                return true;
            }
        }

        // Fallback ultime : aller à la page d'accueil QR
        const homeRoute = this.buildHomeRoute(currentUrl);
        if (homeRoute) {
            this.navigateTo(homeRoute);
            return true;
        }

        return false;
    }

    // Construire la route cible en gardant tenant/pos
    private buildTargetRoute(currentUrl: string, targetPage: string): string | null {
        const qrMatch = currentUrl.match(/\/qrcode\/([^\/]+)\/([^\/]+)/);
        if (qrMatch) {
            const [, tenant, pos] = qrMatch;
            return `/qrcode/${tenant}/${pos}${targetPage}`;
        }
        return null;
    }

    // Construire la route d'accueil
    private buildHomeRoute(currentUrl: string): string | null {
        const qrMatch = currentUrl.match(/\/qrcode\/([^\/]+)\/([^\/]+)/);
        if (qrMatch) {
            const [, tenant, pos] = qrMatch;
            return `/qrcode/${tenant}/${pos}/home`;
        }
        return null;
    }

    // Vérifier si on peut revenir avec la stack
    canGoBack(): boolean {
        return this.historyStack.length > 1;
    }

    // Vérifier si on peut revenir (stack OU logique)
    canGoBackAny(): boolean {
        return this.canGoBack() || this.canGoBackLogical();
    }

    // Vérifier si on peut revenir avec la logique
    private canGoBackLogical(): boolean {
        const currentPageName = this.getCurrentPageName();
        return !!(currentPageName && this.qrCodeNavigationMap[currentPageName]);
    }

    getCurrentEntry(): HistoryEntry | null {
        return this.historyStack.length > 0 ?
            this.historyStack[this.historyStack.length - 1] : null;
    }

    getCurrentPageName(): string | undefined {
        const entry = this.getCurrentEntry();
        if (entry) {
            return entry.route.split('/').pop();
        }

        // Fallback : extraire depuis l'URL actuelle
        const currentUrl = this.router.url;
        const segments = currentUrl.split('/');
        return segments[segments.length - 1] || undefined;
    }

    getPreviousEntry(): HistoryEntry | null {
        return this.historyStack.length > 1 ?
            this.historyStack[this.historyStack.length - 2] : null;
    }

    clearHistory() {
        this.historyStack = [];
    }

    getFullHistory(): HistoryEntry[] {
        return [...this.historyStack];
    }

    initializeWith(route: string, data?: any) {
        if (this.isRouteLoaded) {
            return;
        }
        this.isRouteLoaded = true;
        this.clearHistory();
        this.historyStack.push({
            route,
            timestamp: new Date(),
            data
        });

        history.replaceState(
            { navigationServiceManaged: true },
            '',
            route
        );
    }

    // Méthode pour étendre l'arborescence de navigation
    addNavigationRule(fromPage: string, toPage: string) {
        this.qrCodeNavigationMap[fromPage] = toPage;
    }

    // Debug : voir l'arborescence actuelle
    getNavigationMap(): NavigationMap {
        return { ...this.qrCodeNavigationMap };
    }
}